/* ──────────────────────────────────────────────────────────────────────────
 * AI Stack Assurance — §16.
 *
 * The brief asks for the concept already in the UI to become "a genuine
 * control layer", and states the requirement exactly:
 *
 *   "A command centre must know not only 'Crowd risk = HIGH' but:
 *    'Can I trust the systems producing that assessment?'"
 *
 * So this does not display assurance. It measures it, on the platform's own
 * pipeline, and most of what it finds is bad news — which is the point. An
 * assurance layer that reports green while five of six sectors are
 * unobserved and four hazard types have no feed is not an assurance layer.
 * It is a second thing to be wrong about.
 *
 * ── WHAT IS GENUINELY MEASURED (not simulated) ──
 *   • Engine latency      — actually timed with performance.now()
 *   • Integration gaps    — read from the router's real route table
 *   • Observation gaps    — read from the twin's real camera states
 *   • Hazard coverage     — read from the correlator's real hazard list
 *   • Forecast error/drift— computed from retained predictions vs actuals
 *   • Human overrides     — counted from the actual event log
 *
 * ── WHAT CANNOT BE MEASURED, AND IS REPORTED AS SUCH ──
 *   • False-positive / false-negative rate. There is no ground truth: no
 *     labelled incident set, and no Kumbh has happened. Every product that
 *     quotes a precision figure here without a validation set is quoting a
 *     number it cannot have. This returns null with the reason.
 *
 * That distinction — measured versus unmeasurable — is the substance of the
 * section. Anything else is a status light.
 * ────────────────────────────────────────────────────────────────────────── */

import { routerCoverage } from "../intelligence/router";
import { correlateHazards } from "../geo/services/multiHazard";
import { buildTwin } from "../twin/twin";
import { TRUST } from "../geo/data/provenance";

/* ── Feed registry ─────────────────────────────────────────────────────
 * Every data source the platform would consume in a real deployment, and
 * its honest state here. `SIMULATED` means the platform generates it;
 * `ABSENT` means there is nothing at all. Neither is `LIVE`, and the
 * registry has no way to claim `LIVE` without a real integration. */
export const FEED_STATE = {
  LIVE: "LIVE",
  SIMULATED: "SIMULATED",
  ABSENT: "ABSENT",
  DEGRADED: "DEGRADED",
};

export const FEED_REGISTRY = [
  { id: "FEED-CCTV",      label: { en: "CCTV / video analytics", mr: "सीसीटीव्ही" },        owner: "POLICE",    state: FEED_STATE.ABSENT,    note: "No video pipeline integrated. Camera entities exist in the twin; none carry a stream." },
  { id: "FEED-GIS",       label: { en: "GIS / OpenStreetMap", mr: "जीआयएस" },               owner: "NMC",       state: FEED_STATE.LIVE,      note: "OpenStreetMap basemap tiles are genuinely live over the network." },
  { id: "FEED-GATES",     label: { en: "Entry / exit gate counters", mr: "द्वार मोजणी" },    owner: "POLICE",    state: FEED_STATE.SIMULATED, note: "Gate throughput is modelled, not counted." },
  { id: "FEED-TRAFFIC",   label: { en: "Road traffic", mr: "वाहतूक" },                       owner: "TRANSPORT", state: FEED_STATE.SIMULATED, note: "Derived from the mobility chain, not from road sensors." },
  { id: "FEED-PARKING",   label: { en: "Parking occupancy", mr: "वाहनतळ" },                  owner: "TRANSPORT", state: FEED_STATE.SIMULATED, note: "Modelled from sector population." },
  { id: "FEED-TRANSPORT", label: { en: "Rail / bus schedules", mr: "रेल्वे / बस" },           owner: "TRANSPORT", state: FEED_STATE.SIMULATED, note: "Fixed demo cohorts, not a live timetable feed." },
  { id: "FEED-WEATHER",   label: { en: "Weather", mr: "हवामान" },                            owner: "REVENUE",   state: FEED_STATE.SIMULATED, note: "Deterministic diurnal curve, not an IMD forecast." },
  { id: "FEED-RIVER",     label: { en: "River level", mr: "नदी पातळी" },                     owner: "REVENUE",   state: FEED_STATE.SIMULATED, note: "Modelled lag from rainfall, not a WRD gauge." },
  { id: "FEED-HEALTH",    label: { en: "Health / medical cases", mr: "आरोग्य" },              owner: "HEALTH",    state: FEED_STATE.SIMULATED, note: "Presentation rate derived from heat and density, not reported cases." },
  { id: "FEED-AMBULANCE", label: { en: "Ambulance / 108 dispatch", mr: "रुग्णवाहिका" },       owner: "HEALTH",    state: FEED_STATE.ABSENT,    note: "No dispatch integration. Unit positions are invented." },
  { id: "FEED-POLICE",    label: { en: "Police control room", mr: "पोलीस नियंत्रण" },         owner: "POLICE",    state: FEED_STATE.ABSENT,    note: "No CAD/control-room integration." },
  { id: "FEED-FIRE",      label: { en: "Fire control room", mr: "अग्निशमन नियंत्रण" },        owner: "FIRE",      state: FEED_STATE.ABSENT,    note: "No integration. Fire hazard is therefore unmonitored." },
  { id: "FEED-NDRF",      label: { en: "NDRF deployment", mr: "एनडीआरएफ" },                  owner: "NDRF",      state: FEED_STATE.ABSENT,    note: "No integration. NDRF team positions in the twin are invented placements." },
  { id: "FEED-MUNICIPAL", label: { en: "Municipal services", mr: "नागरी सेवा" },              owner: "NMC",       state: FEED_STATE.SIMULATED, note: "Service utilisation modelled from provision ratios." },
  { id: "FEED-FIELD",     label: { en: "Field officer reports", mr: "क्षेत्र अहवाल" },        owner: "POLICE",    state: FEED_STATE.SIMULATED, note: "Incidents are generated in-browser." },
  { id: "FEED-CITIZEN",   label: { en: "Citizen incident reports", mr: "नागरिक तक्रारी" },    owner: "NMC",       state: FEED_STATE.ABSENT,    note: "No public reporting channel is connected." },
];

/* ── Latency ───────────────────────────────────────────────────────────
 * Genuinely measured. `time()` runs the engine and records how long it
 * actually took, so the latency panel is the one number in this platform
 * that is not modelled. */
const now = () =>
  (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());

export function time(label, fn, budgetMs) {
  const t0 = now();
  let ok = true;
  let result = null;
  let error = null;
  try {
    result = fn();
  } catch (e) {
    ok = false;
    error = e?.message ?? String(e);
  }
  const ms = Number((now() - t0).toFixed(2));
  return {
    label, ms, ok, error, result,
    budgetMs: budgetMs ?? null,
    withinBudget: budgetMs == null ? null : ms <= budgetMs,
    measured: true,
  };
}

/* Budgets. A command centre needs a refresh inside a few hundred
 * milliseconds; anything slower and an operator is acting on a stale
 * picture without knowing it. */
export const LATENCY_BUDGET_MS = {
  forecast: 120,
  hazards: 150,
  interventions: 400,
  emergency: 100,
  mobility: 80,
  twin: 200,
};

/* ── Drift ─────────────────────────────────────────────────────────────
 * Compare forecast error in the recent window against the earlier window.
 * Drift is not a single number: it is a change in a number over time, so
 * it cannot be reported at all until there are two windows to compare.
 * Returning "no drift" before then would be a false all-clear. */
export function assessDrift(accuracy, previousAccuracy) {
  if (!accuracy?.available) {
    return {
      measurable: false,
      reason: accuracy?.reason
        ?? "No forecast has reached its target time yet, so error cannot be computed.",
    };
  }
  if (!previousAccuracy?.available) {
    return {
      measurable: false,
      reason: "Only one measurement window exists. Drift is a change over time and needs two.",
    };
  }
  const byHorizon = accuracy.byHorizon.map((h) => {
    const prev = previousAccuracy.byHorizon.find((p) => p.horizon === h.horizon);
    if (!prev || prev.mape == null || h.mape == null) {
      return { horizon: h.horizon, drift: null, reason: "no comparable earlier window" };
    }
    const delta = Number((h.mape - prev.mape).toFixed(2));
    return {
      horizon: h.horizon,
      currentMape: h.mape,
      previousMape: prev.mape,
      drift: delta,
      /* A model getting worse is the finding. A model getting better is
       * also a finding — it usually means the conditions changed, not that
       * the model improved. */
      direction: delta > 1 ? "degrading" : delta < -1 ? "improving" : "stable",
    };
  });
  return {
    measurable: true,
    byHorizon,
    degrading: byHorizon.filter((h) => h.direction === "degrading"),
  };
}

/* ── The assurance picture ─────────────────────────────────────────────── */
export function assessAssurance({
  liveState,
  twin = null,
  accuracy = null,
  previousAccuracy = null,
  eventLog = [],
  timings = [],
  sessionStartedAt = null,
} = {}) {
  const t = twin ?? (liveState ? buildTwin(liveState) : null);

  /* 1. Data availability — counted from the registry, not asserted. */
  const feeds = FEED_REGISTRY;
  const live = feeds.filter((f) => f.state === FEED_STATE.LIVE);
  const simulated = feeds.filter((f) => f.state === FEED_STATE.SIMULATED);
  const absent = feeds.filter((f) => f.state === FEED_STATE.ABSENT);
  const degraded = feeds.filter((f) => f.state === FEED_STATE.DEGRADED);

  const dataAvailability = {
    total: feeds.length,
    live: live.length,
    simulated: simulated.length,
    absent: absent.length,
    degraded: degraded.length,
    /* The honest headline. One genuinely live feed out of sixteen. */
    statement:
      `${live.length} of ${feeds.length} data sources are genuinely live. `
      + `${simulated.length} are simulated by this platform and ${absent.length} are not connected at all. `
      + `No assessment in this platform is derived from real operational data.`,
    byOwner: [...new Set(feeds.map((f) => f.owner))].map((owner) => ({
      owner,
      total: feeds.filter((f) => f.owner === owner).length,
      connected: feeds.filter((f) => f.owner === owner && f.state === FEED_STATE.LIVE).length,
    })),
    feeds,
  };

  /* 2. Camera / feed health — read from the twin, not from a status table. */
  const gaps = t ? t.observationGaps() : [];
  const cameraHealth = {
    sectors: gaps,
    sectorsObserved: gaps.filter((g) => g.observed).length,
    sectorsTotal: gaps.length,
    camerasMapped: t ? t.byType("camera").length : 0,
    camerasConnected: t ? t.byType("camera").filter((c) => c.connected).length : 0,
    statement: gaps.length
      ? `${gaps.filter((g) => g.observed).length} of ${gaps.length} sectors have a connected camera. Camera positions exist in the twin but no video feed is attached, so every sector is effectively unobserved.`
      : "Twin not supplied; camera health cannot be assessed.",
  };

  /* 3. Model health — forecast error, genuinely computed where available. */
  const modelHealth = accuracy?.available
    ? {
        measurable: true,
        byHorizon: accuracy.byHorizon,
        worstMape: Math.max(...accuracy.byHorizon.map((h) => h.mape ?? 0)),
        samples: accuracy.totalSamples,
        caveat:
          "Scored against what the model itself later produced, not against observed reality. It measures internal consistency, not real-world accuracy.",
      }
    : {
        measurable: false,
        reason: accuracy?.reason ?? "No accuracy measurement has been supplied.",
      };

  /* 4. Latency — the only genuinely measured performance number here. */
  const latency = {
    measured: timings.length > 0,
    timings,
    slowest: timings.length
      ? timings.reduce((a, x) => (x.ms > a.ms ? x : a))
      : null,
    overBudget: timings.filter((x) => x.withinBudget === false),
    failed: timings.filter((x) => !x.ok),
  };

  /* 5. Confidence — provenance, aggregated. Nothing in this platform is
   * VERIFIED, and the assurance layer says so rather than averaging it
   * into a reassuring percentage. */
  const hazards = liveState ? correlateHazards(liveState) : null;
  const confidence = {
    /* Deliberately not a percentage. A single confidence number invites
     * an operator to read 82% as "mostly right", which is not what any
     * of these provenance levels mean. */
    highestTrustPresent: TRUST.SIMULATED,
    verifiedSources: 0,
    statement:
      "No output in this platform carries VERIFIED provenance. Every figure is SIMULATED or derived from simulated inputs. A single aggregate confidence score is deliberately not produced, because it would imply a level of validation that does not exist.",
  };

  /* 6. Drift. */
  const drift = assessDrift(accuracy, previousAccuracy);

  /* 7. False alerts — the honest null. */
  const falseAlerts = {
    measurable: false,
    rate: null,
    reason:
      "False-positive and false-negative rates cannot be computed. There is no labelled ground-truth incident set for this theatre, and Simhastha 2027 has not happened. Any precision or recall figure quoted here would be fabricated.",
    whatWouldBeNeeded:
      "A validation set of historical incidents with confirmed outcomes, or a controlled exercise with independently recorded ground truth.",
  };

  /* 8. System availability — session uptime, genuinely observed. */
  const availability = sessionStartedAt
    ? {
        measurable: true,
        sessionMinutes: Number(((Date.now() - sessionStartedAt) / 60000).toFixed(1)),
        note: "Browser session uptime only. This is not a service-availability figure — there is no service.",
      }
    : {
        measurable: false,
        reason: "No session start time supplied.",
      };

  /* 9. Integration failures — from the router's real route table. */
  const router = routerCoverage();
  const integrations = {
    paradigms: router.paradigmCount,
    attached: router.attached,
    gaps: router.byParadigm.filter((p) => !p.attached),
    statement: router.statement,
  };

  /* 10. Human overrides — counted from the actual event log. */
  const overrides = countOverrides(eventLog);

  /* ── The verdict ────────────────────────────────────────────────────
   * Assembled from findings rather than from a score, because a score
   * lets a reader skip the findings. */
  const findings = [];
  if (absent.length) {
    findings.push({
      severity: "critical",
      area: "data",
      text: `${absent.length} data sources are not connected, including CCTV, ambulance dispatch and the police control room.`,
    });
  }
  if (cameraHealth.sectorsObserved === 0 && cameraHealth.sectorsTotal > 0) {
    findings.push({
      severity: "critical",
      area: "observation",
      text: "No sector has a connected camera. Every crowd figure in this platform is modelled, not observed.",
    });
  }
  if (hazards?.coverage?.blind) {
    findings.push({
      severity: "high",
      area: "coverage",
      text: hazards.coverage.statement,
    });
  }
  if (!falseAlerts.measurable) {
    findings.push({
      severity: "high",
      area: "validation",
      text: "Alert precision has never been measured and cannot be, with the data available.",
    });
  }
  if (latency.overBudget.length) {
    findings.push({
      severity: "medium",
      area: "latency",
      text: `${latency.overBudget.length} engine(s) exceeded their latency budget: ${latency.overBudget.map((x) => x.label).join(", ")}.`,
    });
  }
  if (latency.failed.length) {
    findings.push({
      severity: "critical",
      area: "latency",
      text: `${latency.failed.length} engine(s) threw during measurement: ${latency.failed.map((x) => `${x.label} (${x.error})`).join(", ")}.`,
    });
  }
  if (drift.measurable && drift.degrading.length) {
    findings.push({
      severity: "medium",
      area: "drift",
      text: `Forecast error is increasing at ${drift.degrading.map((d) => `+${d.horizon}m`).join(", ")}.`,
    });
  }
  if (integrations.gaps.length) {
    findings.push({
      severity: "medium",
      area: "integration",
      text: `${integrations.gaps.length} computational paradigm(s) have no engine attached: ${integrations.gaps.map((g) => g.paradigm.label.en).join(", ")}.`,
    });
  }

  const order = { critical: 0, high: 1, medium: 2, low: 3 };
  findings.sort((a, b) => order[a.severity] - order[b.severity]);

  return {
    dataAvailability,
    cameraHealth,
    modelHealth,
    latency,
    confidence,
    drift,
    falseAlerts,
    availability,
    integrations,
    overrides,
    findings,
    /* The one-line answer to the brief's question. */
    trustStatement:
      "This platform's assessments should be treated as a demonstration of method, not as an operational picture. "
      + `${live.length} of ${feeds.length} data sources are live, no sector is observed by a connected camera, and alert precision has never been measured. `
      + "The reasoning is inspectable and the arithmetic is sound; the inputs are not real.",
    /* Convenience for §9's comms hazard. */
    totalFeeds: feeds.length,
    degradedFeeds: absent.length + degraded.length,
  };
}

/* Human overrides — the brief lists these as an assurance signal, and it
 * is the right instinct: a system whose recommendations are always accepted
 * is either perfect or not being read. Both deserve investigation. */
export function countOverrides(eventLog = []) {
  const decisions = eventLog.filter((e) =>
    ["authorised", "rejected", "modified", "cancelled"].includes(e.type ?? e.to),
  );
  const rejected = decisions.filter((e) => (e.type ?? e.to) === "rejected").length;
  const modified = decisions.filter((e) => (e.type ?? e.to) === "modified").length;
  const approved = decisions.filter((e) => (e.type ?? e.to) === "authorised").length;
  const total = decisions.length;

  if (!total) {
    return {
      measurable: false,
      reason: "No authorisation decisions have been recorded in this session yet.",
    };
  }

  const overrideRate = Number(((rejected + modified) / total).toFixed(2));
  return {
    measurable: true,
    total, approved, rejected, modified, overrideRate,
    /* Both extremes are findings. */
    finding:
      overrideRate === 0
        ? "Every recommendation was accepted unmodified. A system that is never overridden is either perfect or not being read; neither should be assumed."
        : overrideRate > 0.5
          ? "More than half of recommendations were rejected or modified. The decision engine is not matching operational judgement."
          : null,
  };
}

export const ASSURANCE_ASSUMPTIONS = [
  "Engine latency is genuinely measured with performance.now(). It is the only performance figure in this platform that is not modelled.",
  "Feed states are declared in a registry with no way to claim LIVE without a real integration. Fifteen of sixteen are simulated or absent.",
  "Model accuracy is scored against the model's own later output, not observed reality. It measures internal consistency and is labelled as such everywhere it appears.",
  "Drift needs two measurement windows and reports as unmeasurable until it has them. Reporting 'no drift' from a single window would be a false all-clear.",
  "False-positive and false-negative rates are reported as null with the reason. There is no ground truth, and any precision figure quoted here would be fabricated.",
  "No aggregate confidence percentage is produced. A single number would imply a level of validation that does not exist.",
];
